import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { api } from "@/lib/api";
import { X, Sparkles } from "lucide-react";
import { toast } from "sonner";

const STORAGE_KEY = "bf_customer_prompt";
const DELAY_MS = 4500;

export default function WelcomeSheet() {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const state = localStorage.getItem(STORAGE_KEY);
    if (state) return; // already dismissed or submitted
    const t = setTimeout(() => setOpen(true), DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, "dismissed");
    setOpen(false);
  };

  const submit = async () => {
    if (!name.trim() || phone.replace(/[^0-9]/g, "").length < 8) {
      toast.error("Please enter your name and WhatsApp number");
      return;
    }
    setSaving(true);
    try {
      await api.post("/customers/self-register", {
        name: name.trim(),
        whatsapp_number: phone.trim(),
      });
      localStorage.setItem(STORAGE_KEY, "submitted");
      setOpen(false);
      toast.success("Thanks — we'll notify you about new arrivals 💌");
    } catch {
      toast.error("Couldn't save — please try again");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="absolute inset-0 z-50 bg-black/40"
          onClick={dismiss}
          data-testid="welcome-sheet-overlay"
        >
          <motion.div
            initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 32, stiffness: 260 }}
            className="absolute bottom-0 inset-x-0 bg-white rounded-t-3xl p-6 pb-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <img src="/logo-full.png" alt="" className="h-9 w-auto" />
                <div className="text-[9px] tracking-editorial uppercase" style={{ color: "var(--ink-3)" }}>Welcome to Butterfly</div>
              </div>
              <button
                data-testid="welcome-close-btn"
                onClick={dismiss}
                className="w-8 h-8 rounded-full grid place-items-center hover:bg-black/5 press"
                aria-label="Close"
              >
                <X size={14} />
              </button>
            </div>

            <h2 className="font-display text-[26px] leading-[1.05] mt-5" style={{ color: "var(--ink)" }}>
              First to know,
              <br />
              <span className="font-display-italic" style={{ color: "var(--accent)" }}>always the first to have.</span>
            </h2>
            <p className="text-[12px] mt-2.5" style={{ color: "var(--ink-2)" }}>
              Leave your WhatsApp — we send a curated note when new pieces arrive. No spam, ever.
            </p>

            <div className="mt-5 space-y-2.5">
              <input
                data-testid="welcome-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="w-full h-12 rounded-2xl border border-black/10 px-4 text-sm outline-none focus:border-neutral-900"
              />
              <input
                data-testid="welcome-phone-input"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                inputMode="tel"
                placeholder="WhatsApp number (with country code)"
                className="w-full h-12 rounded-2xl border border-black/10 px-4 text-sm outline-none focus:border-neutral-900"
              />
            </div>

            <button
              data-testid="welcome-submit-btn"
              onClick={submit}
              disabled={saving}
              className="mt-4 w-full h-12 rounded-full text-[12px] tracking-editorial-sm uppercase press disabled:opacity-60"
              style={{ background: "var(--ink)", color: "var(--bg)" }}
            >
              {saving ? "Saving..." : "Get updates"}
            </button>
            <button
              data-testid="welcome-skip-btn"
              onClick={dismiss}
              className="mt-2 w-full text-[11px] tracking-editorial-sm uppercase py-2 press"
              style={{ color: "var(--ink-3)" }}
            >
              Maybe later
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
